import type { ReportMetricRow } from "@core/components/ReportTreeTable";
import {
  parseGridKpiValue,
  parseSelectKpiValue,
} from "@core/reports/utils/fieldTypeUtils";

export function getNumericValue(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  // grid_rollup / other structured objects are never scalars.
  if (typeof value === "object") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (trimmed === "") return null;

  // Structured KPI JSON must not be treated as numeric.
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return null;

  const numericValue = Number(trimmed);
  return Number.isNaN(numericValue) ? null : numericValue;
}

export function getBooleanValue(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
    if (normalized === "yes") return true;
    if (normalized === "no") return false;
  }

  return null;
}

export function formatBooleanValue(value: boolean): string {
  return value ? "Yes" : "No";
}

function tryParseJsonObject(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
}

/** Discriminated parent/TOTAL GRID cell from department-report merged rollup. */
export type GridRollupValue = {
  kind: "grid_rollup";
  column_totals: Record<string, number>;
  column_labels: Record<string, string>;
  column_order: string[];
  show_row_totals: boolean;
  row_total: number | null;
  /** Stacked free rows then fixed rows collapsed by row_key. */
  rows: Array<Record<string, unknown>>;
};

export function isGridRollupValue(value: unknown): boolean {
  const obj = tryParseJsonObject(value);
  return obj !== null && obj.kind === "grid_rollup";
}

export function parseGridRollupValue(value: unknown): GridRollupValue | null {
  const obj = tryParseJsonObject(value);
  if (!obj || obj.kind !== "grid_rollup") return null;

  const columnTotals: Record<string, number> = {};
  if (obj.column_totals && typeof obj.column_totals === "object" && !Array.isArray(obj.column_totals)) {
    for (const [key, raw] of Object.entries(obj.column_totals as Record<string, unknown>)) {
      if (raw === null || raw === undefined || raw === "") continue;
      const n = typeof raw === "number" ? raw : Number(raw);
      if (Number.isFinite(n)) columnTotals[String(key)] = n;
    }
  }

  const columnLabels: Record<string, string> = {};
  if (obj.column_labels && typeof obj.column_labels === "object" && !Array.isArray(obj.column_labels)) {
    for (const [key, raw] of Object.entries(obj.column_labels as Record<string, unknown>)) {
      if (raw != null && String(raw).trim()) columnLabels[String(key)] = String(raw);
    }
  }

  const columnOrder = Array.isArray(obj.column_order)
    ? (obj.column_order as unknown[]).map((key) => String(key)).filter(Boolean)
    : [];

  const rows: Array<Record<string, unknown>> = [];
  if (Array.isArray(obj.rows)) {
    for (const raw of obj.rows) {
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
      rows.push({ ...(raw as Record<string, unknown>) });
    }
  }

  const showRowTotals = Boolean(obj.show_row_totals);
  let rowTotal: number | null = null;
  if (obj.row_total !== null && obj.row_total !== undefined && obj.row_total !== "") {
    const n = typeof obj.row_total === "number" ? obj.row_total : Number(obj.row_total);
    if (Number.isFinite(n)) rowTotal = n;
  }

  return {
    kind: "grid_rollup",
    column_totals: columnTotals,
    column_labels: columnLabels,
    column_order: columnOrder,
    show_row_totals: showRowTotals,
    row_total: showRowTotals ? rowTotal : null,
    rows,
  };
}

function orderedKeysForRollup(rollup: GridRollupValue): string[] {
  const keys: string[] = [];
  for (const key of rollup.column_order) {
    if (key in rollup.column_totals && !keys.includes(key)) keys.push(key);
  }
  for (const key of Object.keys(rollup.column_totals)) {
    if (!keys.includes(key)) keys.push(key);
  }
  return keys;
}

function formatRollupNumber(n: number): string {
  return n.toLocaleString();
}

/** Internal GRID row fields — never show as columns or labeled preview cells. */
export function isGridRowMetaKey(key: string): boolean {
  return key === "row_kind" || key === "row_key";
}

export const GRID_ROLLUP_ROW_TOTAL_KEY = "__row_total";

export type GridDisplayMeta = {
  rows: Array<Record<string, string>>;
  columnLabels: Record<string, string>;
  columnOrder: string[];
  /** When true, the last body row is the totals / entry-style totals row. */
  emphasizeLastRow?: boolean;
};

function resolveRollupColumnOrder(rollup: GridRollupValue): string[] {
  const keys: string[] = [];
  for (const key of rollup.column_order) {
    if (key && !isGridRowMetaKey(key) && !keys.includes(key)) keys.push(key);
  }
  if (keys.length === 0) {
    for (const key of Object.keys(rollup.column_labels)) {
      if (key && !isGridRowMetaKey(key) && !keys.includes(key)) keys.push(key);
    }
  }
  for (const key of Object.keys(rollup.column_totals)) {
    if (key && !isGridRowMetaKey(key) && !keys.includes(key)) keys.push(key);
  }
  for (const row of rollup.rows) {
    for (const key of Object.keys(row)) {
      if (key && !isGridRowMetaKey(key) && !keys.includes(key)) keys.push(key);
    }
  }
  return keys;
}

function formatRollupCellDisplay(raw: unknown): string {
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "boolean") return raw ? "Yes" : "No";
  if (typeof raw === "number" && Number.isFinite(raw)) return formatRollupNumber(raw);
  const text = String(raw).trim();
  if (text === "") return "";
  const n = Number(text);
  if (text !== "" && Number.isFinite(n) && /^-?\d+(\.\d+)?$/.test(text)) {
    return formatRollupNumber(n);
  }
  return String(raw);
}

function rowHorizontalTotal(
  row: Record<string, unknown>,
  rollupKeys: string[]
): number | null {
  let sum = 0;
  let had = false;
  for (const key of rollupKeys) {
    const raw = row[key];
    if (raw === null || raw === undefined || raw === "") continue;
    let n: number | null = null;
    if (typeof raw === "boolean") n = raw ? 1 : 0;
    else if (typeof raw === "number" && Number.isFinite(raw)) n = raw;
    else {
      const text = String(raw).trim().toLowerCase();
      if (text === "true" || text === "1") n = 1;
      else if (text === "false" || text === "0") n = 0;
      else if (text !== "" && Number.isFinite(Number(text))) n = Number(text);
    }
    if (n !== null) {
      sum += n;
      had = true;
    }
  }
  return had ? sum : null;
}

/**
 * Parent/TOTAL Read more table: stacked free rows, collapsed fixed rows, Total footer.
 * Returns null when there is nothing to show (no rows and no totals).
 */
export function gridMetaFromRollup(value: unknown): GridDisplayMeta | null {
  const rollup = parseGridRollupValue(value);
  if (!rollup) return null;

  const hasBodyRows = rollup.rows.length > 0;
  const hasTotals =
    Object.keys(rollup.column_totals).length > 0 ||
    (rollup.show_row_totals && rollup.row_total !== null);
  if (!hasBodyRows && !hasTotals) return null;

  const columnOrder = resolveRollupColumnOrder(rollup);
  const columnLabels: Record<string, string> = { ...rollup.column_labels };
  const showCorner = rollup.show_row_totals && rollup.row_total !== null;
  const rollupKeys = Object.keys(rollup.column_totals);
  if (showCorner) {
    if (!columnOrder.includes(GRID_ROLLUP_ROW_TOTAL_KEY)) {
      columnOrder.push(GRID_ROLLUP_ROW_TOTAL_KEY);
    }
    columnLabels[GRID_ROLLUP_ROW_TOTAL_KEY] = "Total";
  }

  const headingKey = columnOrder[0] ?? "";
  const rows: Array<Record<string, string>> = [];

  for (const source of rollup.rows) {
    const display: Record<string, string> = {};
    for (const key of columnOrder) {
      if (key === GRID_ROLLUP_ROW_TOTAL_KEY) {
        const horiz = rowHorizontalTotal(source, rollupKeys);
        display[key] = horiz !== null ? formatRollupNumber(horiz) : "";
      } else {
        display[key] = formatRollupCellDisplay(source[key]);
      }
    }
    rows.push(display);
  }

  if (hasTotals) {
    const totalsRow: Record<string, string> = {};
    for (const key of columnOrder) {
      if (key === headingKey) {
        totalsRow[key] = "Total";
      } else if (key === GRID_ROLLUP_ROW_TOTAL_KEY) {
        totalsRow[key] = formatRollupNumber(rollup.row_total as number);
      } else if (key in rollup.column_totals) {
        totalsRow[key] = formatRollupNumber(rollup.column_totals[key]);
      } else {
        totalsRow[key] = "";
      }
    }
    rows.push(totalsRow);
  }

  return {
    rows,
    columnLabels,
    columnOrder,
    ...(hasTotals ? { emphasizeLastRow: true } : {}),
  };
}

/** Column-totals line: "A: 38, B: 14, C: 14" (keys present in column_totals only). */
export function formatGridRollupColumnTotalsLine(value: unknown): string | null {
  const rollup = parseGridRollupValue(value);
  if (!rollup) return null;
  const keys = orderedKeysForRollup(rollup);
  if (keys.length === 0) return "";
  return keys
    .map((key) => `${columnLabelForKey(key, rollup.column_labels)}: ${formatRollupNumber(rollup.column_totals[key])}`)
    .join(", ");
}

/**
 * Compact parent/TOTAL in-cell and PDF display: column totals (+ corner Total).
 */
export function formatGridRollupValue(value: unknown): string | null {
  const rollup = parseGridRollupValue(value);
  if (!rollup) return null;

  const lines: string[] = [];
  const totalsLine = formatGridRollupColumnTotalsLine(rollup);
  if (totalsLine) lines.push(totalsLine);

  if (rollup.show_row_totals && rollup.row_total !== null) {
    lines.push(`Total: ${formatRollupNumber(rollup.row_total)}`);
  }

  if (lines.length === 0 && rollup.rows.length === 0) return "";

  return lines.join("\n");
}

/** Human-readable SELECT / MULTISELECT cell text (never raw JSON). */
export function formatSelectMultiselectValue(value: unknown): string | null {
  const obj = tryParseJsonObject(value);
  if (!obj || !Array.isArray(obj.option_ids)) return null;

  const parsed = parseSelectKpiValue(obj);
  if (parsed.option_ids.length === 0) return "";

  const labels = parsed.option_ids.map((id) => {
    const otherText = parsed.inputs?.[String(id)]?.trim();
    if (otherText) return otherText;
    const fromParsed = parsed.option_labels?.[String(id)]?.trim();
    if (fromParsed) return fromParsed;
    const labelMap = obj.option_labels;
    if (labelMap && typeof labelMap === "object" && !Array.isArray(labelMap)) {
      const label = (labelMap as Record<string, unknown>)[String(id)];
      if (label != null && String(label).trim()) return String(label);
    }
    return `Option ${id}`;
  });

  return labels.join(", ");
}

function gridMetaFromValue(value: unknown): GridDisplayMeta | null {
  const obj = tryParseJsonObject(value);
  // Parent grid_rollup DTO is not a leaf entry grid — never treat as rows payload.
  if (!obj || obj.kind === "grid_rollup" || !Array.isArray(obj.rows)) return null;

  const parsed = parseGridKpiValue(obj);
  const columnLabels: Record<string, string> = { ...(parsed.column_labels ?? {}) };
  if (obj.column_labels && typeof obj.column_labels === "object" && !Array.isArray(obj.column_labels)) {
    for (const [k, v] of Object.entries(obj.column_labels as Record<string, unknown>)) {
      if (!columnLabels[k] && v != null && String(v).trim()) {
        columnLabels[k] = String(v);
      }
    }
  }

  const columnOrder =
    parsed.column_order?.length
      ? [...parsed.column_order]
      : Array.isArray(obj.column_order)
        ? (obj.column_order as unknown[]).map((k) => String(k)).filter(Boolean)
        : [];

  const rows = (parsed.rows as Array<Record<string, string>>).map((row) => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(row)) {
      if (isGridRowMetaKey(k)) continue;
      out[k] = v == null ? "" : String(v);
    }
    return out;
  });

  return { rows, columnLabels, columnOrder };
}

function orderedKeysForRow(
  row: Record<string, string>,
  columnOrder: string[],
  columnLabels: Record<string, string>
): string[] {
  const keys: string[] = [];
  for (const key of columnOrder) {
    if (isGridRowMetaKey(key)) continue;
    if (key in row || key in columnLabels) keys.push(key);
  }
  for (const key of Object.keys(row)) {
    if (isGridRowMetaKey(key) || keys.includes(key)) continue;
    keys.push(key);
  }
  for (const key of Object.keys(columnLabels)) {
    if (isGridRowMetaKey(key) || keys.includes(key)) continue;
    keys.push(key);
  }
  return keys;
}

function columnLabelForKey(key: string, columnLabels: Record<string, string>): string {
  const label = columnLabels[key]?.trim();
  if (label) return label;
  return key
    .replace(/^col_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Full indented grid text for PDF / modal fallback. Never formats grid_rollup. */
export function formatGridValue(value: unknown): string | null {
  if (isGridRollupValue(value)) return null;
  const meta = gridMetaFromValue(value);
  if (!meta) return null;
  if (meta.rows.length === 0) return "";

  return meta.rows
    .map((row, index) => {
      const keys = orderedKeysForRow(row, meta.columnOrder, meta.columnLabels);
      const lines = keys
        .map((key) => {
          const cell = row[key];
          if (cell == null || String(cell).trim() === "") return null;
          return `  ${columnLabelForKey(key, meta.columnLabels)}: ${cell}`;
        })
        .filter((line): line is string => Boolean(line));
      if (lines.length === 0) return `Row ${index + 1}\n  —`;
      return `Row ${index + 1}\n${lines.join("\n")}`;
    })
    .join("\n\n");
}

/** First-row labeled summary for table cells. Never formats grid_rollup. */
export function formatGridFirstRowPreview(value: unknown): string | null {
  if (isGridRollupValue(value)) return null;
  const meta = gridMetaFromValue(value);
  if (!meta) return null;
  if (meta.rows.length === 0) return "";

  const row = meta.rows[0] ?? {};
  const keys = orderedKeysForRow(row, meta.columnOrder, meta.columnLabels);
  const parts = keys
    .map((key) => {
      const cell = row[key];
      if (cell == null || String(cell).trim() === "") return null;
      return `${columnLabelForKey(key, meta.columnLabels)}: ${cell}`;
    })
    .filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(", ") : "—";
}

export function getGridDisplayMeta(value: unknown): GridDisplayMeta | null {
  const meta = gridMetaFromValue(value);
  if (!meta || meta.rows.length === 0) return null;
  return meta;
}

export function formatStructuredKpiValue(value: unknown): string | null {
  const selectText = formatSelectMultiselectValue(value);
  if (selectText !== null) return selectText;
  const rollupText = formatGridRollupValue(value);
  if (rollupText !== null) return rollupText;
  const gridText = formatGridFirstRowPreview(value);
  if (gridText !== null) return gridText;
  return null;
}

/** Full structured text for PDF (grids use indented dump, not first-row preview). */
export function formatStructuredKpiValueForPdf(value: unknown): string | null {
  const selectText = formatSelectMultiselectValue(value);
  if (selectText !== null) return selectText;
  const rollupText = formatGridRollupValue(value);
  if (rollupText !== null) return rollupText;
  const gridText = formatGridValue(value);
  if (gridText !== null) return gridText;
  return null;
}

export function stringifyStructuredValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;

  const structured = formatStructuredKpiValueForPdf(value);
  if (structured !== null) return structured;

  if (Array.isArray(value)) {
    const renderedItems = value
      .map((item) => getViewableValueText(item) ?? stringifyStructuredValue(item))
      .filter((item): item is string => Boolean(item && item.trim()));

    if (renderedItems.length > 0) {
      return renderedItems.join("\n\n");
    }
  }

  if (typeof value === "object") {
    const preferredKeys = [
      "text",
      "message",
      "content",
      "summary",
      "remarks",
      "notes",
      "description",
      "label",
      "value",
    ] as const;

    for (const key of preferredKeys) {
      const nestedValue = (value as Record<string, unknown>)[key];
      const preferredText = getViewableValueText(nestedValue);
      if (preferredText) return preferredText;
    }

    if (
      "option_ids" in (value as object) ||
      "rows" in (value as object) ||
      (value as { kind?: unknown }).kind === "grid_rollup"
    ) {
      return formatStructuredKpiValueForPdf(value);
    }

    try {
      const json = JSON.stringify(value, null, 2);
      return json && json !== "{}" && json !== "[]" ? json : null;
    } catch {
      return null;
    }
  }

  return null;
}

export const VIEWABLE_TEXT_PREVIEW_LENGTH = 100;

export function truncateViewableText(
  text: string,
  maxLength = VIEWABLE_TEXT_PREVIEW_LENGTH
): { text: string; isTruncated: boolean } {
  if (text.length <= maxLength) {
    return { text, isTruncated: false };
  }
  return { text: `${text.slice(0, maxLength)}…`, isTruncated: true };
}

export function getViewableValueText(value: unknown): string | null {
  if (value === null || value === undefined) return null;

  const booleanValue = getBooleanValue(value);
  if (booleanValue !== null) {
    return formatBooleanValue(booleanValue);
  }

  const structured = formatStructuredKpiValue(value);
  if (structured !== null) {
    return structured.trim() === "" ? null : structured;
  }

  if (typeof value === "string") {
    return value.trim() !== "" && getNumericValue(value) === null ? value : null;
  }

  if (typeof value === "number") return null;

  return stringifyStructuredValue(value);
}

function collectBooleanEntries(row: ReportMetricRow, field: string): boolean[] {
  const entries: boolean[] = [];
  const booleanValue = getBooleanValue(row?.[field]);

  if (booleanValue !== null) {
    entries.push(booleanValue);
  }

  for (const child of row.subRows ?? []) {
    entries.push(...collectBooleanEntries(child, field));
  }

  return entries;
}

function inlineBooleanText(row: ReportMetricRow, field: string): string | null {
  const entries = collectBooleanEntries(row, field);
  if (entries.length === 0) return null;

  const [first] = entries;
  return entries.every((e) => e === first) ? formatBooleanValue(first) : "";
}

function sumChildren(row: ReportMetricRow, field: string): number {
  return (row.subRows ?? []).reduce((acc, r) => acc + (getNumericValue(r?.[field]) ?? 0), 0);
}

function avgChildren(row: ReportMetricRow, field: string): number | null {
  const values = (row.subRows ?? [])
    .map((r) => getNumericValue(r?.[field]))
    .filter((n): n is number => n !== null);
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function flattenAll(tree: ReportMetricRow[]): ReportMetricRow[] {
  const out: ReportMetricRow[] = [];
  const walk = (rows: ReportMetricRow[]) => {
    for (const row of rows) {
      out.push(row);
      if (row.subRows?.length) walk(row.subRows);
    }
  };
  walk(tree);
  return out;
}

/** True when any row in the tree has viewable text for `field`. */
export function columnHasViewableText(tree: ReportMetricRow[], field: string): boolean {
  return flattenAll(tree).some((row) => Boolean(getViewableValueText(row?.[field])));
}

export type PdfCellFormat = {
  text: string;
  align: "left" | "right";
};

function resolveRollupMode(
  field: string,
  row: ReportMetricRow,
  tree: ReportMetricRow[]
): string {
  const fromRow = (row as { __rollup_modes?: Record<string, string> }).__rollup_modes?.[field];
  if (fromRow) return String(fromRow).toUpperCase();
  const root = tree[0] as { __rollup_modes?: Record<string, string> } | undefined;
  const fromRoot = root?.__rollup_modes?.[field];
  if (fromRoot) return String(fromRoot).toUpperCase();
  return "SUM";
}

function aggregateChildren(
  row: ReportMetricRow,
  field: string,
  tree: ReportMetricRow[]
): number | null {
  const mode = resolveRollupMode(field, row, tree);
  if (mode === "NONE") return null;
  if (mode === "AVG") return avgChildren(row, field);
  return sumChildren(row, field);
}

/** Format one tree-table cell for PDF export (mirrors ReportTreeTable semantics). */
export function formatTreeCellForPdf(
  row: ReportMetricRow,
  field: string,
  tree: ReportMetricRow[]
): PdfCellFormat {
  const booleanText = inlineBooleanText(row, field);
  if (booleanText !== null) {
    return { text: booleanText, align: "right" };
  }

  // Parent/TOTAL: always render grid_rollup (never scalar sum / blank text).
  const rollupText = formatGridRollupValue(row?.[field]);
  if (rollupText !== null) {
    return { text: rollupText, align: "left" };
  }

  const isParent = (row.subRows?.length ?? 0) > 0;
  const ownText = (() => {
    const gridFull = formatGridValue(row?.[field]);
    if (gridFull !== null) {
      return gridFull.trim() === "" ? null : gridFull;
    }
    return getViewableValueText(row?.[field]);
  })();

  if (!isParent && ownText) {
    return { text: ownText, align: "left" };
  }

  if (columnHasViewableText(tree, field)) {
    return { text: "", align: "left" };
  }

  const n = isParent ? aggregateChildren(row, field, tree) : getNumericValue(row?.[field]);
  return { text: n === null ? "" : n.toLocaleString(), align: "right" };
}

/** Format a TOTAL-row cell for PDF export. */
export function formatTotalsCellForPdf(
  value: unknown,
  field: string,
  tree: ReportMetricRow[]
): PdfCellFormat {
  // TOTAL: prefer grid_rollup over blanking the column for child text/grids.
  const rollupText = formatGridRollupValue(value);
  if (rollupText !== null) {
    return { text: rollupText, align: "left" };
  }

  if (columnHasViewableText(tree, field)) {
    return { text: "", align: "left" };
  }

  if (value === null || value === undefined) {
    return { text: "", align: "right" };
  }

  const structured = formatStructuredKpiValueForPdf(value);
  if (structured !== null) {
    return { text: structured, align: "left" };
  }

  const booleanValue = getBooleanValue(value);
  if (booleanValue !== null) {
    return { text: formatBooleanValue(booleanValue), align: "right" };
  }

  const numericValue = getNumericValue(value);
  if (numericValue !== null) {
    return { text: numericValue.toLocaleString(), align: "right" };
  }

  return { text: typeof value === "string" ? value : "", align: "right" };
}

/** Inline boolean when all subtree entries agree; blank when mixed. */
export function getInlineBooleanCellText(row: ReportMetricRow, field: string): string | null {
  return inlineBooleanText(row, field);
}

/** Parent aggregation helper used by ReportTreeTable (honors __rollup_modes when present). */
export function getAggregatedNumericValue(
  row: ReportMetricRow,
  field: string,
  tree: ReportMetricRow[] = []
): number | null {
  // Never bag-sum GRID parents that carry grid_rollup.
  if (isGridRollupValue(row?.[field])) return null;
  const isParent = (row.subRows?.length ?? 0) > 0;
  if (!isParent) return getNumericValue(row?.[field]);
  return aggregateChildren(row, field, tree.length ? tree : [row]);
}
